/**
 * SetupAccountPage (Doc 50) — public route accessible via invitation link.
 *
 * URL: `#/setup-account?token=<raw>`. Workflow:
 * 1. Parse token from URL.
 * 2. GET /auth/setup-account/preview → email + display_name + ttl.
 * 3. User enters password (≥8) + confirm + optional display_name.
 * 4. POST /auth/setup-account → consume token + auto-login.
 */
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  User as UserIcon,
} from "lucide-react";
import * as React from "react";

import { authApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BrandLogo, GradientText } from "@/components/ui/gradient";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/stores/auth";

function fmtTtl(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return "đã hết hạn";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days} ngày ${hours}h`;
  const mins = Math.floor(seconds / 60);
  if (mins > 60) return `${hours}h ${mins % 60}p`;
  return `${mins} phút`;
}

function getTokenFromUrl(): string {
  if (typeof window === "undefined") return "";
  const hash = window.location.hash;
  // Format: #/setup-account?token=XXX
  const queryIdx = hash.indexOf("?");
  if (queryIdx === -1) return "";
  const params = new URLSearchParams(hash.slice(queryIdx + 1));
  return params.get("token") ?? "";
}

export function SetupAccountPage(): JSX.Element {
  const setAuth = useAuthStore((s) => s.setAuth);
  const [token] = React.useState(() => getTokenFromUrl());
  const [preview, setPreview] = React.useState<{
    email: string;
    display_name: string | null;
    expires_in_seconds: number | null;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(true);
  const [previewError, setPreviewError] = React.useState<string | null>(null);

  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  // Load preview
  React.useEffect(() => {
    if (!token) {
      setPreviewError("Không có token trong URL.");
      setPreviewLoading(false);
      return;
    }
    void (async () => {
      try {
        const data = await authApi.setupAccountPreview(token);
        if (data === null) {
          setPreviewError(
            "Token không hợp lệ hoặc đã hết hạn. Liên hệ admin để cấp lại.",
          );
        } else {
          setPreview(data);
          setDisplayName(data.display_name ?? "");
        }
      } catch (e) {
        setPreviewError(
          e instanceof ApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : String(e),
        );
      } finally {
        setPreviewLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitError(null);
    if (password.length < 8) {
      setSubmitError("Mật khẩu ≥ 8 ký tự");
      return;
    }
    if (password !== confirm) {
      setSubmitError("Mật khẩu xác nhận không khớp");
      return;
    }
    setSubmitting(true);
    try {
      const tokens = await authApi.setupAccount(
        token,
        password,
        displayName.trim() || undefined,
      );
      useAuthStore.setState({ accessToken: tokens.access_token });
      const me = await authApi.me();
      setAuth(me, tokens.access_token);
      // Clear hash → app router takes over
      window.location.hash = "/chat";
    } catch (e) {
      setSubmitError(
        e instanceof ApiError
          ? e.status === 401
            ? "Token không hợp lệ hoặc đã được dùng. Liên hệ admin cấp lại."
            : e.message
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-mesh p-4 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-40 -left-40 size-96 rounded-full bg-gradient-brand opacity-20 blur-3xl"
      />
      <Card className="w-full max-w-md relative z-10 shadow-card-lg backdrop-blur-xl bg-background/95">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-3">
            <BrandLogo size={72} />
          </div>
          <CardTitle className="text-xl">
            <GradientText as="span" className="font-bold">
              Thiết lập tài khoản
            </GradientText>
          </CardTitle>
          <CardDescription>
            Hoàn tất setup để bắt đầu dùng AI Agent
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {previewLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Đang xác thực
              token…
            </div>
          ) : previewError ? (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 space-y-2">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="size-4 shrink-0 mt-0.5" />
                <span>{previewError}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Vui lòng liên hệ admin để được cấp lại invitation link, hoặc{" "}
                <a
                  href="/#/"
                  className="text-primary underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.hash = "/";
                  }}
                >
                  về trang đăng nhập
                </a>
                .
              </p>
            </div>
          ) : preview ? (
            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
              <div className="rounded-md border bg-muted/40 p-3 space-y-1 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="size-4" /> {preview.email}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="size-3" /> Token hết hạn sau:{" "}
                  <span className="font-medium tabular-nums">
                    {fmtTtl(preview.expires_in_seconds)}
                  </span>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Tên hiển thị
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="VD: Nguyễn Văn A"
                    className="pl-9"
                    maxLength={255}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Mật khẩu (≥ 8 ký tự)
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9 pr-9"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                  >
                    {showPassword ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Xác nhận mật khẩu
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="pl-9"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </div>
              </div>

              {submitError ? (
                <div className="rounded-md bg-destructive/10 border border-destructive/30 p-2 text-sm text-destructive">
                  {submitError}
                </div>
              ) : null}

              <Button
                type="submit"
                variant="brand"
                className="w-full h-10"
                disabled={
                  submitting ||
                  password.length < 8 ||
                  password !== confirm
                }
              >
                {submitting
                  ? "Đang xử lý..."
                  : "Hoàn tất & đăng nhập"}
              </Button>
            </form>
          ) : null}
        </CardContent>

        <CardFooter className="text-[10px] text-center text-muted-foreground justify-center">
          Doc 50 · Single-use invitation token · cuonglv_agent_ai
        </CardFooter>
      </Card>
    </div>
  );
}
